import { ScreenWidthType } from 'components/types'
import { LanguageType } from 'providers/ConnectedIntl/languages'
import { screenWidthLookup } from 'utils'

import * as AT from './actionTypes'
import {
  CountryType,
  ErrorType,
  generateNotificationId,
  ModalNameType,
  ModalOptionsType,
  ModalOrigin,
  NotificationIdType,
  NotificationType,
  RawNotificationType,
  UsStateType,
  VeriffMetadataType,
  WidgetType
} from './types'
import * as zendesk from './Zendesk/actions'

export type HideModalActionType = {
  payload: { readonly delay: number; readonly name: ModalNameType }
  type: typeof AT.HIDE_MODAL
}

export type ShowModalActionType = {
  payload: {
    readonly name: ModalNameType
    readonly options?: ModalOptionsType
  }
  type: typeof AT.SHOW_MODAL
}

export type ReadyActionType = {
  type: typeof AT.READY
}

export type AppActionType =
  | {
      payload: { notification: NotificationType }
      type: typeof AT.ADD_NOTIF
    }
  | { type: typeof AT.BOOTSTRAP }
  | { type: typeof AT.GET_COUNTRIES }
  | { type: typeof AT.GET_LOGIN_MESSAGING }
  | { type: typeof AT.GET_VERIFF_METADATA }
  | HideModalActionType
  | ShowModalActionType
  | ReadyActionType
  | { type: typeof AT.REMOVE_EXPIRED_NOTIF }
  | {
      payload: { id: NotificationIdType }
      type: typeof AT.REMOVE_NOTIF
    }
  | {
      payload: { readonly countries: Array<CountryType> }
      type: typeof AT.SET_COUNTRIES
    }
  | {
      payload: { isDismissable: boolean }
      type: typeof AT.SET_MODAL_DISMISSABLE
    }
  | {
      payload: { fullscreen: boolean }
      type: typeof AT.SET_MODAL_FULLSCREEN
    }
  | {
      payload: { largescreen: boolean }
      type: typeof AT.SET_MODAL_LARGESCREEN
    }
  | {
      payload: { width?: ScreenWidthType }
      type: typeof AT.SET_SCREEN_SIZE
    }
  | {
      payload: { readonly states: Array<UsStateType> }
      type: typeof AT.SET_US_STATES
    }
  | {
      payload: { readonly veriffMetadata: VeriffMetadataType }
      type: typeof AT.SET_VERIFF_METADATA
    }
  | {
      payload: { readonly veriffMetadataError: ErrorType }
      type: typeof AT.SET_VERIFF_METADATA_ERROR
    }
  | {
      payload: { language: LanguageType }
      type: typeof AT.SET_LANGUAGE
    }
  | {
      payload: { widget: WidgetType }
      type: typeof AT.TOGGLE_WIDGET_EXPANDED
    }

const addNotif = (notification: Partial<RawNotificationType>): AppActionType => ({
  payload: {
    notification: {
      created: +new Date(),
      duration: 2,
      id: generateNotificationId(),
      title: 'KO_GENERIC',
      type: 'default',
      ...notification
    }
  },
  type: AT.ADD_NOTIF
})

const bootstrap = (): AppActionType => ({
  type: AT.BOOTSTRAP
})

const getCountries = (): AppActionType => ({
  type: AT.GET_COUNTRIES
})

const getLoginMessaging = (): AppActionType => ({
  type: AT.GET_LOGIN_MESSAGING
})

const getVeriffMetadata = (): AppActionType => ({
  type: AT.GET_VERIFF_METADATA
})

const hideModal = (name: ModalNameType): AppActionType => ({
  payload: { delay: 300, name },
  type: AT.HIDE_MODAL
})

const ready = (): AppActionType => ({
  type: AT.READY
})

const removeExpiredNotif = (): AppActionType => ({
  type: AT.REMOVE_EXPIRED_NOTIF
})

const removeNotif = (id: NotificationIdType): AppActionType => ({
  payload: { id },
  type: AT.REMOVE_NOTIF
})

const setScreenSize = (rect: ClientRect): AppActionType => {
  const width = screenWidthLookup(rect)

  return {
    payload: {
      width
    },
    type: AT.SET_SCREEN_SIZE
  }
}

const setCountries = (countries: Array<CountryType>): AppActionType => ({
  payload: { countries },
  type: AT.SET_COUNTRIES
})

const setLanguage = (language: LanguageType): AppActionType => ({
  payload: { language },
  type: AT.SET_LANGUAGE
})

const setModalDismissable = (isDismissable: boolean): AppActionType => ({
  payload: { isDismissable },
  type: AT.SET_MODAL_DISMISSABLE
})

const setModalFullscreen = (fullscreen: boolean): AppActionType => ({
  payload: { fullscreen },
  type: AT.SET_MODAL_FULLSCREEN
})

const setModalLargescreen = (largescreen: boolean): AppActionType => ({
  payload: { largescreen },
  type: AT.SET_MODAL_LARGESCREEN
})

const setUsStates = (states: Array<UsStateType>): AppActionType => ({
  payload: { states },
  type: AT.SET_US_STATES
})

const setVeriffMetadata = (veriffMetadata: VeriffMetadataType): AppActionType => ({
  payload: { veriffMetadata },
  type: AT.SET_VERIFF_METADATA
})

const setVeriffMetadataError = (veriffMetadataError: ErrorType): AppActionType => ({
  payload: { veriffMetadataError },
  type: AT.SET_VERIFF_METADATA_ERROR
})

const showModal = (name: ModalNameType, options?: ModalOptionsType): AppActionType => ({
  payload: {
    name,
    options: options ?? {
      origin: ModalOrigin.UNKNOWN
    }
  },
  type: AT.SHOW_MODAL
})

const toggleWidgetExpanded = (widget: WidgetType): AppActionType => ({
  payload: { widget },
  type: AT.TOGGLE_WIDGET_EXPANDED
})

export default {
  addNotif,
  bootstrap,
  getCountries,
  getLoginMessaging,
  getVeriffMetadata,
  hideModal,
  ready,
  removeExpiredNotif,
  removeNotif,
  setCountries,
  setLanguage,
  setModalDismissable,
  setModalFullscreen,
  setModalLargescreen,
  setScreenSize,
  setUsStates,
  setVeriffMetadata,
  setVeriffMetadataError,
  showModal,
  toggleWidgetExpanded,
  zendesk
}
